<template>
  <div class="site">
    <SiteHeader />
    <main class="site-main">
      <router-view v-slot="{ Component }">
        <transition name="page" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <SiteFooter />
  </div>
</template>

<script setup>
import SiteHeader from '@/components/SiteHeader.vue'
import SiteFooter from '@/components/SiteFooter.vue'
</script>

<style>
/* ===================================================================
   全局重置 + 设计变量（电竞金 · 深色主题）
   =================================================================== */
:root {
  --bg: #0a0908;
  --bg-soft: #12100d;
  --panel: rgba(255, 255, 255, 0.035);
  --panel-solid: #17131d;
  --panel-2: #1c1712;
  --border: rgba(255, 205, 120, 0.14);
  --border-strong: rgba(255, 205, 120, 0.32);

  --gold: #ffce5c;
  --gold-2: #ff9e2c;
  --gold-deep: #c9741a;
  --crimson: #ff4d4d;

  --text: #f6f1e7;
  --muted: #a99e8c;
  --muted-2: #7b7264;

  --grad-gold: linear-gradient(135deg, #ffe08a 0%, #ff9e2c 55%, #f2771a 100%);
  --grad-panel: linear-gradient(160deg, rgba(255, 205, 120, 0.09), rgba(255, 255, 255, 0.015));

  --radius: 18px;
  --radius-sm: 12px;
  --shadow: 0 18px 44px rgba(0, 0, 0, 0.55);
  --glow: 0 0 0 1px rgba(255, 205, 120, 0.18), 0 16px 40px rgba(255, 158, 44, 0.16);

  --font-display: 'Chakra Petch', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  --font-body: 'PingFang SC', 'Microsoft YaHei', 'Hiragino Sans GB', 'Source Han Sans SC',
    -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  --maxw: 1180px;
}

* {
  box-sizing: border-box;
}

html,
body,
#app {
  margin: 0;
  padding: 0;
  min-height: 100%;
}

body {
  background-color: var(--bg);
  color: var(--text);
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.65;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-image:
    radial-gradient(900px 520px at 12% -6%, rgba(255, 158, 44, 0.18), transparent 60%),
    radial-gradient(760px 460px at 92% 4%, rgba(255, 77, 77, 0.12), transparent 62%),
    radial-gradient(1200px 800px at 50% 120%, rgba(255, 205, 120, 0.07), transparent 60%);
  background-attachment: fixed;
}

h1,
h2,
h3,
h4,
p,
figure {
  margin: 0;
}

a {
  color: inherit;
  text-decoration: none;
}

button {
  font-family: inherit;
  cursor: pointer;
  border: none;
  background: none;
  color: inherit;
}

img {
  max-width: 100%;
  display: block;
}

ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

/* 版心 */
.wrap {
  width: 100%;
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 0 24px;
}

/* 数字/英文展示字体 */
.mono {
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.grad-text {
  background: var(--grad-gold);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* 通用按钮 */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 26px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 600;
  transition: transform 0.18s ease, box-shadow 0.25s ease, background 0.25s ease;
  white-space: nowrap;
}

.btn-primary {
  background: var(--grad-gold);
  color: #241300;
  box-shadow: 0 10px 26px rgba(255, 158, 44, 0.34);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px rgba(255, 158, 44, 0.46);
}

.btn-ghost {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--border-strong);
  color: var(--text);
}

.btn-ghost:hover {
  background: rgba(255, 205, 120, 0.1);
}

/* 面板 */
.panel {
  background: var(--grad-panel);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  backdrop-filter: blur(6px);
}

/* 标签 */
.tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: var(--gold);
  background: rgba(255, 205, 120, 0.1);
  border: 1px solid var(--border);
}

/* 区块标题 */
.section-head {
  text-align: center;
  margin-bottom: 44px;
}

.section-head .eyebrow {
  color: var(--gold-2);
  font-size: 13px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
}

.section-head h2 {
  font-size: clamp(26px, 3.4vw, 40px);
  margin: 12px 0 10px;
  font-weight: 800;
  letter-spacing: 0.01em;
}

.section-head p {
  color: var(--muted);
  font-size: 16px;
}

.site {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.site-main {
  flex: 1;
  padding-bottom: 40px;
}

/* 页面切换动画 */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(14px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* 滚动条 */
::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

::-webkit-scrollbar-track {
  background: #100e0b;
}

::-webkit-scrollbar-thumb {
  background: #3a3126;
  border-radius: 8px;
}

::-webkit-scrollbar-thumb:hover {
  background: #56483a;
}

@media (max-width: 640px) {
  .wrap {
    padding: 0 16px;
  }
  .section-head {
    margin-bottom: 30px;
  }
}
</style>
